// react
import { MouseEvent, useContext, useState } from 'react';

// types
import { Entity, Feedback, Frame, User } from '../../../@types';

// i18n
import { Trans, useTranslation } from 'react-i18next';

// router
import { useNavigate } from 'react-router-dom';

// context
import { ContextBanner, ContextUser } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, Grid, Typography } from '@mui/material';

// dialogs
import { DialogActions } from '../DialogActions/DialogActions';

// props
export interface DialogUpdateUserEntitiesProps {
	action: 'ADD' | 'REMOVE';
	entity: number;
	frame?: Frame;
	isOpen: boolean;
	onClose: (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
	onSuccess?: () => void;
	user: User;
}

export const DialogUpdateUserEntities = ({
	action,
	entity,
	frame,
	isOpen,
	onClose,
	onSuccess,
	user,
}: DialogUpdateUserEntitiesProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const navigate = useNavigate();
	const { t } = useTranslation();

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);
	const { user: userCurrent } = useContext(ContextUser);

	// vars
	const frameTranslationsMap = {
		BENEFICIARY: {
			alerts: {
				success: t('alert.userRemovedFromBeneficiary', { name: user.fullName }),
				warning: t('dialogRemoveBeneficiaryUser.warning'),
			},
			content: 'dialogRemoveBeneficiaryUser.content',
		},
		ENTITY: {
			alerts: {
				success: t('alert.userRemovedFromEntity', { name: user.fullName }),
				warning: t('dialogRemoveEntityUser.warning'),
			},
			content: 'dialogRemoveEntityUser.content',
		},
		SUPPLIER: {
			alerts: {
				success: t('alert.userRemovedFromSupplier', { name: user.fullName }),
				warning: t('dialogRemoveSupplierUser.warning'),
			},
			content: 'dialogRemoveSupplierUser.content',
		},
	};

	const frameKey = frame === 'BENEFICIARY' || frame === 'SUPPLIER' ? frame : 'ENTITY';
	const frameTranslations = frameTranslationsMap[frameKey];

	// state
	const [feedback, setFeedback] = useState<Feedback | null>(null);

	const entitiesCurrent = user.entities.map((e) =>
		typeof e === 'object' ? (e as Entity).id : e
	) as number[];

	const entitiesNew =
		action === 'REMOVE'
			? entitiesCurrent.filter((e) => e !== entity)
			: [...entitiesCurrent, entity];

	// patch user entities
	const { fetchRequest: updateUserEntities, isLoading: isLoadingUpdateUserEntities } = useFetch({
		url: `${process.env.REACT_APP_API_URL}/users/${user.id}`,
		isLazy: true,
		options: {
			method: 'PATCH',
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
			body: JSON.stringify({
				entities: entitiesNew,
				...(project?.id && { projects: [{ id: project.id }] }),
			}),
		},
		onError: () => {
			setFeedback({
				alert: {
					children: t('error.default'),
				},
			});
		},
		onSuccess: () => {
			setBanner({
				hasClose: true,
				message: frameTranslations.alerts.success,
				severity: 'success',
			});

			if (user.id === userCurrent?.id) {
				navigate('/projects');
				return;
			}

			if (onSuccess) {
				onSuccess();
			}

			if (onClose) {
				onClose();
			}
		},
	});

	// handlers
	const handleClose = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		setFeedback(null);

		if (onClose) {
			onClose(e);
		}
	};

	const handleSubmit = () => {
		setFeedback(null);

		updateUserEntities();
	};

	return (
		<DialogActions
			actions={[
				{
					children: t('cancel'),
					onClick: handleClose,
					variant: 'text',
				},
				{
					children: t('removeUser'),
					color: 'error',
					loading: isLoadingUpdateUserEntities,
					onClick: handleSubmit,
					variant: 'contained',
				},
			]}
			alignItems="center"
			hasClose={true}
			IconCircleProps={{
				type: 'user',
			}}
			isOpen={isOpen}
			maxWidth={400}
			onClose={handleClose}
			subtitle={{
				children: user.fullName,
			}}
			title={{
				children: t('dialogRemoveEntityUser.title'),
			}}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Trans
						components={{
							strong: <Typography color="brand.orange.600" component="span" fontWeight={600} />,
						}}
						i18nKey={frameTranslations.content}
						values={{ name: user.fullName }}
					/>
				</Grid>
				<Grid item xs={12}>
					<Alert severity="warning">{frameTranslations.alerts.warning}</Alert>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error">{feedback.alert.children}</Alert>
					</Grid>
				)}
			</Grid>
		</DialogActions>
	);
};
